import React from 'react';
import { SignalList } from './DecisionWidgets';
import BenchmarkTrendPanel from './BenchmarkTrendPanel';
import BenchmarkTrackingPanel from './BenchmarkTrackingPanel';

function formatPct(v, digits = 2) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(digits)}%`;
}

function formatContrib(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(3)}%`;
}

function formatBp(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(1)} bp`;
}

function changeClass(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return 'flat';
  return n > 0 ? 'up' : 'down';
}

const PARSE_QUALITY_LABEL = {
  good: '良好',
  partial: '部分识别',
  unparsed: '未解析',
  empty: '无基准'
};

function renderChangeCell(snap) {
  if (snap.type === 'fixed_range') {
    return `${snap.minPct}%-${snap.maxPct}%`;
  }
  const pct = snap.quote?.change_pct ?? snap.change_pct;
  if (pct != null) {
    const suffix = snap.bond_weather ? ` · 债市${snap.bond_weather}` : '';
    const dateSuffix = snap.trade_date ? ` · ${snap.trade_date}` : '';
    return `${formatPct(pct)}${suffix}${dateSuffix}`;
  }
  return '—';
}

export default function BenchmarkPanel({ analysis, sensitivity, stress }) {
  if (!analysis) return null;
  const snaps = analysis.index_snapshots || [];
  const qualityLabel = PARSE_QUALITY_LABEL[analysis.parseQuality] || analysis.parseQuality || '—';

  return (
    <div className="benchmark-panel">
      <div className="benchmark-raw">
        <div className="decision-muted" style={{ marginBottom: 4 }}>业绩比较基准（原文）</div>
        {analysis.raw || '—'}
      </div>

      {analysis.summary ? (
        <p className="decision-muted">解析：{analysis.summary}</p>
      ) : null}

      <div className="benchmark-meta-row">
        <span>解析质量：{qualityLabel}</span>
        {analysis.weightSum != null ? <span>权重合计 {analysis.weightSum}%</span> : null}
        {analysis.parserVersion ? <span>解析器 v{analysis.parserVersion}</span> : null}
      </div>

      {(analysis.synthetic_return_today != null ||
        analysis.product_change_today != null ||
        analysis.vs_synthetic_bp != null) && (
        <div className="benchmark-synthetic-block">
          <p className="benchmark-synthetic-desc decision-muted">
            把说明书里的业绩比较基准按权重合成「理论日涨幅」，再按产品 T+N 时滞和交易日历与净值涨幅对齐。
          </p>
          <div className="benchmark-synthetic">
            <div className="benchmark-synthetic-item">
              <div className="benchmark-synthetic-label">市场行情 {analysis.market_trade_date || '最新'}</div>
              <div className={`benchmark-synthetic-value ${changeClass(analysis.synthetic_return_today)}`}>
                {analysis.synthetic_return_today_text || formatContrib(analysis.synthetic_return_today)}
              </div>
              <div className="benchmark-synthetic-hint">各成分贡献相加</div>
            </div>
            <div className="benchmark-synthetic-item">
              <div className="benchmark-synthetic-label">受影响净值 {analysis.product_change_date || '最新'}</div>
              <div className={`benchmark-synthetic-value ${changeClass(analysis.product_change_today)}`}>
                {analysis.product_change_today_text || formatContrib(analysis.product_change_today)}
              </div>
              <div className="benchmark-synthetic-hint">当日净值/万收变动</div>
            </div>
            <div className="benchmark-synthetic-item">
              <div className="benchmark-synthetic-label">相对合成基准</div>
              <div className={`benchmark-synthetic-value ${changeClass(analysis.vs_synthetic_bp)}`}>
                {analysis.comparison_date_aligned ? formatBp(analysis.vs_synthetic_bp) : '不可比'}
              </div>
              <div className="benchmark-synthetic-hint">
                {analysis.comparison_date_aligned
                  ? `${analysis.confirm_time || 'T+N'} 交易日历对齐 · 产品 − 合成`
                  : `按${analysis.confirm_time || 'T+N'}应对应 ${analysis.expected_nav_date || '—'}`}
              </div>
            </div>
          </div>
        </div>
      )}

      {!snaps.length ? (
        <p className="decision-muted">暂未识别指数/区间成分，可 Phase 3 结合说明书校正</p>
      ) : (
        <div className="benchmark-table-wrap">
          <p className="benchmark-table-desc decision-muted">
            下表拆解各基准成分：今日涨跌来自指数行情，贡献 = 权重 × 涨跌。表头与行均为静态展示，无 hover 交互。
          </p>
          <table className="decision-table decision-table-static benchmark-table">
            <thead>
              <tr>
                <th>成分</th>
                <th>权重</th>
                <th>今日涨跌</th>
                <th>贡献</th>
              </tr>
            </thead>
            <tbody>
              {snaps.map((snap, idx) => (
                <tr key={`${snap.code || snap.name}-${idx}`}>
                  <td>
                    <div className="benchmark-table-name">{snap.name}</div>
                    {snap.matchedText ? (
                      <div className="benchmark-item-meta">{snap.matchedText}</div>
                    ) : null}
                    {snap.impact_hint ? (
                      <div className="benchmark-hint">{snap.impact_hint}</div>
                    ) : null}
                  </td>
                  <td>{snap.weightPct != null ? `${snap.weightPct}%` : '—'}</td>
                  <td className={changeClass(snap.quote?.change_pct ?? snap.change_pct)}>
                    {renderChangeCell(snap)}
                  </td>
                  <td>{formatContrib(snap.contribution_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BenchmarkTrendPanel history={analysis.synthetic_history} />

      <BenchmarkTrackingPanel analysis={analysis.tracking_analysis} />

      {sensitivity?.length ? (
        <div className="benchmark-sensitivity-wrap">
          <h3 style={{ fontSize: 13, color: '#94a3b8', margin: '8px 0' }}>敏感度（成分 ±1% → 合成 bp）</h3>
          <table className="decision-table decision-table-static benchmark-table">
            <thead>
              <tr>
                <th>成分</th>
                <th>权重</th>
                <th>+1%</th>
                <th>-1%</th>
              </tr>
            </thead>
            <tbody>
              {sensitivity.map((row) => (
                <tr key={row.code || row.name}>
                  <td>{row.name}</td>
                  <td>{row.weightPct != null ? `${row.weightPct}%` : '—'}</td>
                  <td className="up">+{row.plus_one_pct_synthetic_bp}bp</td>
                  <td className="down">{row.minus_one_pct_synthetic_bp}bp</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {stress?.available && stress.scenarios?.length ? (
        <div className="benchmark-stress-wrap">
          <h3 style={{ fontSize: 13, color: '#94a3b8', margin: '8px 0' }}>
            压力测试（利率上行 → 债端，权重 {stress.bond_weight_pct?.toFixed(0)}%）
          </h3>
          <table className="decision-table decision-table-static benchmark-table">
            <thead>
              <tr>
                <th>情景</th>
                <th>合成基准</th>
                <th>产品推算</th>
              </tr>
            </thead>
            <tbody>
              {stress.scenarios.map((row) => (
                <tr key={row.id}>
                  <td>{row.label}</td>
                  <td className="down">{row.synthetic_text}</td>
                  <td className="down">{row.product_mid_text}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {stress.note ? <p className="decision-muted benchmark-stress-note">{stress.note}</p> : null}
        </div>
      ) : null}

      {analysis.data_note ? (
        <p className="decision-muted benchmark-data-note">{analysis.data_note}</p>
      ) : null}

      {analysis.signals?.length ? (
        <div>
          <h3 style={{ fontSize: 13, color: '#94a3b8', margin: '8px 0' }}>基准相关信号</h3>
          <SignalList signals={analysis.signals} />
        </div>
      ) : null}
    </div>
  );
}
